/**
 * Generic, serialisable summary of a {@link MaterializedDataset} for the
 * LLM transcript. Pure and dataset-source-agnostic — any tool that holds
 * a materialised dataset can summarise it here and render the result with
 * `<DatasetPreview>` (the JSX → markdown reconciler turns it into GFM
 * tables the model reads).
 *
 * The output is intentionally JSON-safe (every sample cell is
 * pre-stringified, dates ISO-formatted, numbers rounded, long strings
 * truncated, nested datasets reduced to a placeholder) so it survives the
 * tool-output envelope round-trip and `message.result` can stay sync.
 */
import { materializedToRows } from '#core/lib/expr/runtime/arquero.ts';
import { type ColumnType, describeColumnType } from '#core/lib/expr/schema/types.ts';
import { type Cell, Dataset, isMaterialized, type MaterializedDataset } from '#core/lib/expr/value.ts';

import { roundForDisplay } from '#plugin-chat/components/tools/helpers/round-for-display.ts';
import { type ColumnStats, computeAutoStats } from '#plugin-chat/lib/tool/auto-stats.ts';

/** Default number of sample rows embedded in the preview. */
const DEFAULT_SAMPLE_ROWS = 5;
/** Long string cells are truncated to this many characters in the sample. */
const MAX_STRING_CELL_CHARS = 80;

/** Per-column profile row rendered in the column table. */
export type DatasetColumnSummary = Readonly<{
  name: string;
  /** Precise engine column type (`describeColumnType`). */
  type: string;
  nullCount: number;
  distinctCount: number;
  /** Type-aware stat prose (numeric range/mean, date range, …) or `—`. */
  summary: string;
}>;

/** Serialisable dataset summary consumed by `<DatasetPreview>`. */
export type DatasetSummary = Readonly<{
  rowCount: number;
  columns: readonly DatasetColumnSummary[];
  /** First N rows; every cell pre-stringified + table-safe. */
  sample: readonly Readonly<Record<string, string>>[];
  /** Data-quality signals for the LLM (empty result, all-null column, …). */
  notes: readonly string[];
}>;

export type SummarizeDatasetOptions = Readonly<{
  /** Sample row cap (default 5). */
  sampleRows?: number;
  /** Column names to exclude from the profile + sample (e.g. synthetic ids). */
  omitColumns?: readonly string[];
}>;

const datasetCellShape = (ds: Dataset): string => {
  if (isMaterialized(ds)) {
    const names = Object.keys(ds.columns);
    const rows = names.length === 0 ? 0 : (ds.columns[names[0] as string]?.length ?? 0);
    return `[dataset ${rows}×${names.length}]`;
  }
  return '[dataset lazy]';
};

/** Stringify a single cell into a compact, markdown-table-safe value. */
const formatCellForSample = (cell: Cell | undefined): string => {
  if (cell === null || cell === undefined) {
    return '';
  }
  if (cell instanceof Dataset) {
    return datasetCellShape(cell);
  }
  if (cell instanceof Date) {
    return cell.toISOString();
  }
  if (typeof cell === 'number') {
    return String(roundForDisplay(cell));
  }
  if (typeof cell === 'boolean') {
    return String(cell);
  }
  return cell.length > MAX_STRING_CELL_CHARS ? `${cell.slice(0, MAX_STRING_CELL_CHARS)}…` : cell;
};

/** Canonical distinct-key — type-prefixed so `1` (number) ≠ `"1"` (string). */
const distinctKey = (cell: Cell): string => {
  if (cell instanceof Date) {
    return `d:${cell.getTime()}`;
  }
  if (cell instanceof Dataset) {
    return 'ds';
  }
  return `${typeof cell}:${String(cell)}`;
};

const fmtStat = (n: number | null): string => (n === null ? 'n/a' : String(roundForDisplay(n)));

/** Type-aware one-line stat prose; `—` when no stats are available. */
const columnSummary = (stats: ColumnStats | undefined): string => {
  if (stats === undefined) {
    return '—';
  }
  switch (stats.type) {
    case 'numeric':
      return stats.count === 0 || stats.mean === null
        ? 'no numeric values'
        : `min=${fmtStat(stats.min)} max=${fmtStat(stats.max)} mean=${fmtStat(stats.mean)} median=${fmtStat(stats.median)}`;
    case 'date':
      return `${stats.min || 'n/a'} → ${stats.max || 'n/a'}`;
    case 'mixed':
      return 'mixed types';
    case 'string':
      return '—';
  }
};

/**
 * Summarise any {@link MaterializedDataset} into a serialisable
 * {@link DatasetSummary}: a per-column profile (type, nulls, distinct,
 * type-aware stats), a capped sample of pre-stringified rows, and
 * data-quality notes. Pure — no I/O, no React.
 */
export function summarizeDataset(ds: MaterializedDataset, opts: SummarizeDatasetOptions = {}): DatasetSummary {
  const sampleRows = opts.sampleRows ?? DEFAULT_SAMPLE_ROWS;
  const omit = new Set(opts.omitColumns ?? []);
  const rows = materializedToRows(ds);
  const rowCount = rows.length;
  const colNames = Array.from(ds.schema.keys()).filter((name) => !omit.has(name));
  const statsByCol = computeAutoStats(rows.slice()).columns;

  const columns: DatasetColumnSummary[] = colNames.map((name) => {
    const cells = ds.columns[name] ?? [];
    const present = cells.filter((c) => c !== null && c !== undefined);
    return {
      name,
      type: describeColumnType(ds.schema.get(name) as ColumnType),
      nullCount: cells.length - present.length,
      distinctCount: new Set(present.map(distinctKey)).size,
      summary: columnSummary(statsByCol[name]),
    };
  });

  const sample = rows
    .slice(0, sampleRows)
    .map((row) => Object.fromEntries(colNames.map((n) => [n, formatCellForSample(row[n] as Cell | undefined)])));

  const notes = [
    ...(rowCount === 0 ? ['Query returned no rows.'] : []),
    ...columns
      .filter((c) => rowCount > 0 && c.nullCount === rowCount)
      .map((c) => `Column "${c.name}" is entirely null in all ${rowCount} rows.`),
    ...colNames
      .filter((n) => ds.schema.get(n)?.kind === 'nested-dataset')
      .map((n) => `Column "${n}" holds nested datasets; the sample shows a placeholder.`),
  ];

  return { rowCount, columns, sample, notes };
}
